import { Sprite, Texture, type Container } from 'pixi.js';
import { createRng } from '../core/rng';
import { sceneSeed, type Look } from './look';
import { backdropScale, settleStep, type Settle } from './settle';
import { VANISH, fieldPoint, unitOf } from './view';

export function paintBackdrop(look: Look, level: number, width: number, height: number, dpr: number): HTMLCanvasElement {
  const scale = backdropScale(width, height, dpr);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (ctx === null) return canvas;
  ctx.scale(scale, scale);
  const view = { width, height };
  const vanish = fieldPoint(view, VANISH.x, VANISH.y);
  look.paint(
    {
      ctx,
      w: width,
      h: height,
      cx: width / 2,
      cy: height / 2,
      unit: unitOf(view),
      rng: createRng(sceneSeed(look.seed, level)),
      level: Math.max(1, level),
      font: look.font,
      vanish,
    },
    look,
  );
  return canvas;
}

export interface Backdrop {
  sync(width: number, height: number, level: number): void;
  destroy(): void;
}

export function createBackdrop(parent: Container, look: Look): Backdrop {
  const sprite = new Sprite(Texture.EMPTY);
  parent.addChild(sprite);
  const state: Settle = { painted: '', pending: '', stable: 0 };
  let level = 0;
  return {
    sync(width, height, next) {
      if (width <= 0 || height <= 0) return;
      const w = Math.round(width);
      const h = Math.round(height);
      const key = `${String(w)}x${String(h)}@${String(next)}`;
      sprite.width = width;
      sprite.height = height;
      if (!settleStep(state, key, next !== level)) return;
      level = next;
      const old = sprite.texture;
      sprite.texture = Texture.from(paintBackdrop(look, next, w, h, window.devicePixelRatio), true);
      sprite.width = width;
      sprite.height = height;
      if (old !== Texture.EMPTY) old.destroy(true);
    },
    destroy() {
      const old = sprite.texture;
      sprite.destroy();
      if (old !== Texture.EMPTY) old.destroy(true);
    },
  };
}
